import { useEffect, useRef } from 'react';
import EditorJS from '@editorjs/editorjs';
import Paragraph from '@editorjs/paragraph';
import Header from '@editorjs/header';
import List from '@editorjs/list';

interface RichTextEditorProps {
  content: string;
  onChange: (content: string) => void;
}

export function RichTextEditor({ content, onChange }: RichTextEditorProps) {
  const editorRef = useRef<EditorJS | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!containerRef.current || editorRef.current) return;

    const editor = new EditorJS({
      holder: containerRef.current,
      data: {
        blocks: [
          {
            type: 'paragraph',
            data: {
              text: content
            }
          }
        ]
      },
      tools: {
        paragraph: {
          class: Paragraph,
          inlineToolbar: true
        },
        header: {
          class: Header,
          config: {
            levels: [2, 3],
            defaultLevel: 2
          }
        },
        list: {
          class: List,
          inlineToolbar: true
        }
      },
      onChange: async () => {
        const savedData = await editor.save();
        const html = savedData.blocks
          .map(block => {
            switch (block.type) {
              case 'paragraph':
                return `<p>${block.data.text}</p>`;
              case 'header':
                return `<h${block.data.level}>${block.data.text}</h${block.data.level}>`;
              case 'list':
                const items = block.data.items
                  .map((item: string) => `<li>${item}</li>`)
                  .join('');
                return block.data.style === 'ordered' ? 
                  `<ol>${items}</ol>` : 
                  `<ul>${items}</ul>`;
              default:
                return block.data.text;
            }
          })
          .join('');
        onChange(html);
      }
    });

    editorRef.current = editor;

    return () => {
      if (editorRef.current && editorRef.current.destroy) {
        editorRef.current.destroy();
        editorRef.current = null;
      }
    };
  }, [content, onChange]);

  return (
    <div 
      ref={containerRef}
      className="min-h-[120px] max-h-[300px] overflow-y-auto p-4 border rounded-md border-[var(--color-border)] bg-[var(--color-bg-card)]"
    />
  );
}